import type React from "react"
import { cn } from "../../lib/utils"

interface PageContainerProps {
	/**
	 * `default` — the design's ~1100px content column (max-w-6xl), used by
	 * every property/project/list page. `narrow` — focused single-column
	 * forms (profile, property new/edit/transfer).
	 */
	variant?: "default" | "narrow"
	/**
	 * `mobileBar` — pages that mount the mobile-only PropertyTopBar
	 * (ZPL-353): top padding clears the 76px fixed mobile chrome (28px
	 * MobileTopHeader + 48px bar); from md up the content clears the fixed
	 * 56px PageActionsBar strip pinned at the viewport top. `bare` — pages without the bar
	 * (dashboard, profile, my-tickets, property-new).
	 */
	chrome?: "mobileBar" | "bare"
	/**
	 * Squeezes the content right of the open 360px members-chat rail. Must
	 * override the gutters at BOTH md and lg — a bare md:pr loses to the
	 * base lg:px-12 in stylesheet order and the content never shrinks.
	 */
	chatRailOpen?: boolean
	className?: string
	children: React.ReactNode
}

/**
 * The one page scaffold (ZPL-349): column width, gutters, and vertical
 * rhythm are defined here and nowhere else, so sibling pages never disagree
 * and tab switches never shift the column. Gutters follow the design's
 * editorial margins: 24px on mobile, 48px from `lg` up.
 */
export function PageContainer({
	variant = "default",
	chrome = "mobileBar",
	chatRailOpen = false,
	className,
	children,
}: PageContainerProps) {
	return (
		<div
			className={cn(
				"flex-1 mx-auto w-full px-6 lg:px-12 pb-24",
				variant === "default" ? "max-w-6xl" : "max-w-2xl",
				chrome === "mobileBar" ? "pt-24 md:pt-20" : "pt-12",
				chatRailOpen && "md:pr-[calc(360px+1.5rem)] lg:pr-[calc(360px+3rem)]",
				className,
			)}
		>
			{children}
		</div>
	)
}
